import MarkdownIt from 'markdown-it';
import { markdownItTable } from 'markdown-it-table';

import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import { MarkdownParser } from '@atlaskit/editor-prosemirror/markdown';
import { Node, type Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';

import { type RegexStorage } from '../../linking-platform/utils';
import mdPluginCitation from '../md-plugin-citation';
import mdPluginDataSculptor from '../md-plugin-data-sculptor';
import mdPluginExpandable from '../md-plugin-expand';
import mdPluginGroupedCitations from '../md-plugin-grouped-citations';
import mdPluginInlineLumina from '../md-plugin-inline-lumina';
import {
	createLinkifyPlugin,
	type FindFilepathsFn,
	type IsLinkInMatchesFn,
} from '../md-plugin-linkify';
import mdPluginListBlockquoteFallback from '../md-plugin-list-blockquote-fallback';
import mdPluginMedia from '../md-plugin-media';
import mdPluginMentions from '../md-plugin-mention';
import { createSmartLinksPlugin } from '../md-plugin-smartlink';
import mdPluginTableBreakTags from '../md-plugin-table-break-tags';
import mdPluginVisualizationChart from '../md-plugin-visualization-chart';

import { mdToPmSchema } from './mappings';
import { sanitizeMarkdown } from './sanitize-markdown';
import { enableSchemaPlugins, filterSchemaMapping } from './utils';

type PluginConfig = {
	linkify: {
		findFilepaths: FindFilepathsFn;
		isLinkInMatches: IsLinkInMatchesFn;
	};
	smartLinks: {
		regexStorage: RegexStorage;
	};
};

export class AiMateMarkdownTransformer extends MarkdownTransformer {
	public parser: MarkdownParser;

	constructor(
		schema: Schema,
		pluginConfig: PluginConfig,
		streaming?: boolean,
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
		shouldShowAsInline?: boolean,
		useGroupedCitations?: boolean,
		xpcWrapUrl?: (url: string, stripOnly: boolean) => string,
	) {
		super();

		const md = MarkdownIt('zero', { html: false });

		md.enable([
			// Process html entity - &#123;, &#xAF;, &quot;, ...
			'entity',
			// Process escaped chars and hardbreaks
			'escape',
			'newline',
		]);

		enableSchemaPlugins(md, schema);

		// Handle blockquotes inside list items - ADF doesn't support this nesting,
		// so we transform them into emphasized paragraphs
		md.use(mdPluginListBlockquoteFallback);

		// enable modified version of linkify plugin
		// @see https://product-fabric.atlassian.net/browse/ED-3097
		md.use(createLinkifyPlugin(pluginConfig.linkify));
		if (useGroupedCitations) {
			md.use(mdPluginGroupedCitations);
		} else {
			md.use(mdPluginCitation);
		}

		if (fg('rovo_enable_data_sculptor')) {
			md.use(mdPluginDataSculptor);
		}

		md.use(mdPluginExpandable);

		md.use(mdPluginVisualizationChart);

		if (fg('rovo_chat_enable_inline_lumina')) {
			md.use(mdPluginInlineLumina);
		}
		// Convert links with Linking Platform supported URLs to Smart Links
		if (
			!streaming &&
			(schema.nodes.inlineCard || schema.nodes.blockCard || schema.nodes.embedCard)
		) {
			// this is where we should separate it into its own plugin called createSmartLinksPluginRefresh
			md.use(
				createSmartLinksPlugin(
					pluginConfig.smartLinks,
					isExperimentEnabled,
					shouldShowAsInline,
					xpcWrapUrl,
				),
			);
		}

		// Convert links matching people directory URLs to mentions
		if (schema.nodes.mention) {
			md.use(mdPluginMentions);
		}

		// Table support
		if (schema.nodes.table) {
			md.use(markdownItTable);

			if (fg('rovo_chat_enable_markdown_table_break_tags')) {
				md.use(mdPluginTableBreakTags);
			}
		}

		// Image support
		if (schema.nodes.media && schema.nodes.mediaSingle) {
			md.use(mdPluginMedia);
		}

		const filter = filterSchemaMapping(schema, mdToPmSchema);

		this.parser = new MarkdownParser(schema, md, filter);
	}

	parse(content: string): Node {
		// Sanitize the raw markdown string before it reaches the markdown-it block
		// tokenizer (ParserBlock.tokenize). Indented tables and 4+ space-indented
		// fences can cause catastrophic backtracking / OOM; `sanitizeMarkdown`
		// outdents fences with 4+ spaces of indentation so the parser recognises
		// them as fenced code blocks rather than entering an infinite tokenization
		// loop.
		//
		// Note: md.core.ruler cannot be used here — core rules run AFTER block
		// tokenization, which is too late to prevent the crash. The sanitization
		// must happen on the raw string before parse() is called.
		const node = this.parser.parse(sanitizeMarkdown(content));

		if (!node) {
			return new Node();
		}

		return node;
	}
}
